import React, { Suspense } from "react";
import { Route, Routes, Navigate, Outlet } from "react-router-dom";
import PropType from "prop-types";

import MainWrapper from "./MainWrapper";
import FallbackComponent from "../../shared/components/FallbackComponent";
import { withMetaDecorator } from "../../shared/components/HOC";
import { meta } from "../../shared/strings/constants";

import { useAuth } from "../../shared/providers/AuthProvider";
import { fetchRegions } from "../../redux/actions/regionActions";
import { useDispatch, useSelector } from "react-redux";
import OrganizationSettings from "screens/OrganizationSettings";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { isUserCitySelector, isUserOrganisationSelector, isUserTenantSelector } from "redux/selectors/user";
import TenantStores from "screens/TenantStores";
import { TenantLayout } from "containers/Layout/TenantLayout";
import usersHooks from "hooks/users.hooks";
import cityHooks from "hooks/city.hooks";
import { logoutCurrentSession } from "services/impersonation";
import persistor from "../../redux/store";
import { addTenantProductRoute, editTenantProductRoute, routes } from "./routes";

const OrganisationLogin = React.lazy(() => import("../Account/LogIn/index"));

const CityLogin = (props) => <OrganisationLogin {...props} />;

const RegisterPagesManager = React.lazy(() => import("../Account/Register/RegisterPagesManager"));

const OrganisationDashboard = React.lazy(() => import("../Dashboards/Organisation/index"));

const UserPage = React.lazy(() => import("../UserPage"));
const OrganisationPage = React.lazy(() => import("../OrganisationPage"));

const CityDashboard = React.lazy(() => import("../Dashboards/City/index"));

const OrganisationForgotPassword = React.lazy(() => import("../Account/ForgotPassword/index"));

const ProfileOrganisation = React.lazy(() => import("../Account/Profile/Organisation/index"));
const ProfileCity = React.lazy(() => import("../Account/Profile/City/index"));
const PostalCodeMap = React.lazy(() => import("../PostalcodeMap/index"));
const ProfileTenant = React.lazy(() => import("../Account/Profile/Tenant/index"));

const Branch = React.lazy(() => import("../Branch/index"));
const Categories = React.lazy(() => import("../Categories/index"));
const Notification = React.lazy(() => import("../Notifications/index"));
const Badge = React.lazy(() => import("../Badges/index"));
const ChallengeRecommendationRules = React.lazy(() => import("../ChallengeRecommendationRules/index"));
const News = React.lazy(() => import("../News/index"));
const HappyHours = React.lazy(() => import("../HappyHours/index"));
const NewsDetails = React.lazy(() => import("../News/edit"));
const Email = React.lazy(() => import("../Emails/index"));

const NotificationDetails = React.lazy(() => import("../Notifications/edit"));

const BranchDetails = React.lazy(() => import("../Branch/edit"));
const OrganisationFAQ = React.lazy(() => import("../FAQ/index"));
const CityKBS = React.lazy(() => import("../KBS/cityKBS"));

const ChallengeOrganisationEdit = React.lazy(() => import("../Challenge/Organisation/edit"));
const ChallengeOrganisationInfo = React.lazy(() => import("../Challenge/Organisation/info"));
const ChallengeOrganisationCreate = React.lazy(() => import("../Challenge/Organisation/create"));
const ChallengeOrganisationCreateForAnotherOrg = React.lazy(() => import("../Challenge/Organisation/CreateForAnotherOrg"));
const InterOrganisationChallengeGuide = React.lazy(() => import("../Challenge/Organisation/InterOrganisationChallengeGuide"));
const ChallengeOrganisationMobilityDuel = React.lazy(() => import("../Challenge/Organisation/duel"));
const ChallengeOrganisationDashboard = React.lazy(() => import("../Challenge/Organisation/index"));
const ChallengeOrganisationList = React.lazy(() => import("../Challenge/Organisation/list"));

const ChallengeCityEdit = React.lazy(() => import("../Challenge/City/edit"));
const ChallengeCityInfo = React.lazy(() => import("../Challenge/City/info"));
const ChallengeCityCreate = React.lazy(() => import("../Challenge/City/create"));
const ChallengeCityDashboard = React.lazy(() => import("../Challenge/City/index"));
const ChallengeCityList = React.lazy(() => import("../Challenge/City/list"));

const TenantDashboard = React.lazy(() => import('screens/TenantDashboard'));
const TenantOrders = React.lazy(() => import('screens/TenantOrders'));
const TenantOrder = React.lazy(() => import('screens/TenantOrder'));
const TenantProducts = React.lazy(() => import('screens/TenantProducts'));
const TenantAddProduct = React.lazy(() => import('screens/TenantAddProduct'));
const TenantEditProduct = React.lazy(() => import('screens/TenantEditProduct'));
const TenantProductBarcodes = React.lazy(() => import('screens/TenantProductBarcodes'));
const TenantRegisterByAdminScreen = React.lazy(() => import('screens/TenantRegisterByAdminScreen'));
const TenantKBS = React.lazy(() => import("../KBS/TenantKBS"));

const CityManage = React.lazy(() => import('screens/CityManage'));
const CitySettings = React.lazy(() => import('screens/CitySettings'));
const ClientIntegrationChecklist = React.lazy(() => import('screens/ClientIntegrationChecklist'));
const CityOrganisations = React.lazy(() => import('screens/CityOrganizationsList'));
const CityUsers = React.lazy(() => import('screens/CityUsersList'));
const CityUsersSessions = React.lazy(() => import('screens/CityUsersSessionsList'));
const CityMaps = React.lazy(() => import('screens/CityMaps'));
const CitySessionsHeatMap = React.lazy(() => import('screens/CitySessionsHeatMap'));
const CitySessionsHeatMapLegacy = React.lazy(() => import('screens/CitySessionsHeatMapLegacy'));
const MobileReportsDashboard = React.lazy(() => import('screens/MobileReportsDashboard'));
const CityMailerLite = React.lazy(() => import('screens/CityMailerLite'));
const ReportsDashboard = React.lazy(() => import('screens/ReportsDashboard'));
const SocialDashboard = React.lazy(() => import('screens/SocialDashboard'));

const CarpoolEvents = React.lazy(() => import('screens/CarpoolEvents'));
const SingleCarpoolEvent = React.lazy(() => import('screens/SingleCarpoolEvent'));
const OrganisationZones = React.lazy(() => import('screens/OrganisationZones'));

const TenantLogin = (props) => <OrganisationLogin {...props} tenantOnly />;

export { addTenantProductRoute, editTenantProductRoute, routes };

export const BreadcrumbsForRoutes = {
  [routes.tenant.dashboard]: {
    icon: DashboardIcon,
    name: "Tableau de bord",
  },
  [routes.tenant.productList]: {
    icon: LocalOfferOutlinedIcon,
    name: "Produits",
  },
  [routes.tenant.tenantKBS]: {
    icon: LocalOfferOutlinedIcon,
    name: "KBS",
  },
  [routes.tenant.orders]: {
    icon: ShoppingCartOutlinedIcon,
    name: "Commandes",
  },
  [addTenantProductRoute]: {
    icon: PostAddIcon,
    name: "Ajouter"
  }
};

const TenantRoutes = () => {
  const isOrganisation = useSelector(isUserOrganisationSelector);
  const [, user] = useAuth();

  if (!user?.role) {
    return <FallbackComponent />;
  }

  if (isOrganisation) {
    return <Navigate to={routes.organisation.dashboard} replace />
  }

  if (user.role !== 'tenant' && user.role !== 'city') {
    return <Navigate to={routes.tenant.login} replace />
  }

  return (
    <TenantLayout>
      <Suspense fallback={<FallbackComponent />}>
        <Outlet />
      </Suspense>
    </TenantLayout>
  );
};

const AUTH_LINK_IN_PROGRESS_KEY = "auth_link_in_progress";
const AUTH_REGISTRATION_IN_PROGRESS_KEY = "auth_registration_in_progress";
const AUTH_REGISTRATION_ERROR_KEY = "auth_registration_error";
const AUTH_REGISTRATION_METHOD_KEY = "auth_registration_method";
const AUTH_REGISTRATION_TARGET_PATH_KEY = "auth_registration_target_path";
const ORGANISATION_REGISTER_PATH = routes.register.entity.replace(
  ":entity",
  "organisation"
);


const CityRoutes = () => {
  const isOrganisation = useSelector(isUserOrganisationSelector);
  const isTenant = useSelector(isUserTenantSelector);

  if (isOrganisation) {
    return <Navigate to={routes.organisation.dashboard} replace />
  }
  if (isTenant) {
    return <Navigate to={routes.tenant.dashboard} replace />
  }

  return (
    <Suspense fallback={<FallbackComponent />}>
      <Outlet />
    </Suspense>
  );
};


const OrganizationRoutes = () => {
  const isTenant = useSelector(isUserTenantSelector);
  const isCity = useSelector(isUserCitySelector);

  if (isTenant) {
    return <Navigate to={routes.tenant.dashboard} replace />
  }
  if (isCity) {
    return <Navigate to={routes.city.dashboard} replace />
  }

  return (
    <Suspense fallback={<FallbackComponent />}>
      <Outlet />
    </Suspense>
  );
};

const useDecoratedRouteComponent = (Component, metaDecorator) =>
  React.useMemo(
    () => (Component ? withMetaDecorator(Component, metaDecorator) : () => null),
    [Component, metaDecorator],
  );

const RegularRoute = ({
  component: Component = null,
  meta: metaDecorator = { title: "Greenplay", description: "Greenplay" },
}) => {
  const Main = useDecoratedRouteComponent(Component, metaDecorator);
  return <Main />;
};

const ProtectedRoute = ({
  component: Component = null,
  meta: metaDecorator = { title: "Greenplay", description: "Greenplay" },
  redirectTo,
  path = "/",
  ...otherProps
}) => {
  // Check if is authenticated or not
  // If NOT redirect to login

  const [userID] = useAuth();
  const { details } = usersHooks.useExternalUser();
  const { limitSettings } = cityHooks.useFetchCityLimitSettings(details?.cityId);
  const Main = useDecoratedRouteComponent(Component, metaDecorator);
  const isAuthRegistrationInProgress =
    sessionStorage.getItem(AUTH_REGISTRATION_IN_PROGRESS_KEY) === "true";
  const hasAuthRegistrationError =
    !!sessionStorage.getItem(AUTH_REGISTRATION_ERROR_KEY);
  const hasAuthRegistrationMethod =
    !!sessionStorage.getItem(AUTH_REGISTRATION_METHOD_KEY);
  const authRegistrationTargetPath =
    sessionStorage.getItem(AUTH_REGISTRATION_TARGET_PATH_KEY) ||
    ORGANISATION_REGISTER_PATH;

  if (
    !userID &&
    (isAuthRegistrationInProgress ||
      hasAuthRegistrationError ||
      hasAuthRegistrationMethod)
  ) {
    return <Navigate to={authRegistrationTargetPath} replace />;
  }

  if (userID) {
    return <Main />;
  }

  console.log('[ProtectedRoute] No userID, redirecting to login');

  const showOnlyCarpool = limitSettings?.c19_carpooling_app?.carpool_events;

  return (
    <Navigate
      to={redirectTo || (
        showOnlyCarpool
          ? routes.organisation.carpoolEvents
          : routes.organisation.login
      )}
      replace
    />
  );

};

const TenantPublicRoute = ({
  component: Component = null,
  meta: metaDecorator = { title: "Greenplay", description: "Greenplay" },
}) => {
  const [userID, user] = useAuth();

  if (!userID) {
    const Main = withMetaDecorator(Component, metaDecorator);
    return <Main />;
  }

  if (!user?.role) {
    return <FallbackComponent />;
  }

  if (user.role === 'tenant') {
    return <Navigate to={routes.tenant.dashboard} replace />;
  }

  const Main = withMetaDecorator(Component, metaDecorator);
  return <Main />;
};

const PreventedRoute = ({
  component: Component = null,
  meta: metaDecorator = { title: "Greenplay", description: "Greenplay" },
  ...otherProps
}) => {
  // Check if is already logged in
  // If YES redirect to Dashboard

  const [userID, user] = useAuth();
  const isAuthLinkInProgress =
    sessionStorage.getItem(AUTH_LINK_IN_PROGRESS_KEY) === "true";
  const isAuthRegistrationInProgress =
    sessionStorage.getItem(AUTH_REGISTRATION_IN_PROGRESS_KEY) === "true";
  const hasAuthRegistrationError =
    !!sessionStorage.getItem(AUTH_REGISTRATION_ERROR_KEY);
  const hasAuthRegistrationMethod =
    !!sessionStorage.getItem(AUTH_REGISTRATION_METHOD_KEY);
  //const history = useHistory();

  //console.log(history.location.pathname);


  // Treat undefined/null/empty as unauthenticated to avoid race before auth loads
  if (
    !userID ||
    isAuthLinkInProgress ||
    isAuthRegistrationInProgress ||
    hasAuthRegistrationError ||
    hasAuthRegistrationMethod
  ) {
    const Main = withMetaDecorator(Component, metaDecorator);
    return <Main />;
  }

  const redirectPath = () => {
    switch (user.role) {
      case 'city':
        return routes.city.dashboard;

      case 'tenant':
        return routes.tenant.dashboard;

      default:
        return otherProps.showOnlyCarpool ? routes.organisation.carpoolEvents : routes.organisation.dashboard;
    }
  }
  return (
    <Navigate to={redirectPath()} replace />
  );
};

const Router = () => {
  const dispatch = useDispatch();
  const [, authUser] = useAuth();

  React.useEffect(() => {
    dispatch(fetchRegions());
  }, [dispatch]);
  const { details, userId: userID } = usersHooks.useExternalUser();

  const { limitSettings } = cityHooks.useFetchCityLimitSettings(details?.cityId);
  const showOnlyCarpool = limitSettings?.c19_carpooling_app?.carpool_events;

  return (
    <MainWrapper>
      <Suspense fallback={<div style={{ padding: 16 }}>Loading…</div>}>
        <Routes>
          <Route path="/logout" element={<LogoutRoute />} />
          <Route path="/" element={
            userID ? (
              authUser?.role === 'city' ? (
                <Navigate to={routes.city.dashboard} replace />
              ) : authUser?.role === 'tenant' ? (
                <Navigate to={routes.tenant.dashboard} replace />
              ) : showOnlyCarpool ? (
                <Navigate to={routes.organisation.carpoolEvents} replace />
              ) : (
                <Navigate to={routes.organisation.dashboard} replace />
              )
            ) : (
              <Navigate to={routes.organisation.login} replace />
            )
          } />

          <Route path={routes.organisation.login} element={<PreventedRoute component={OrganisationLogin} meta={meta.organisation.Login} showOnlyCarpool={showOnlyCarpool} />} />
          <Route path={routes.tenant.login} element={<TenantPublicRoute component={TenantLogin} meta={meta.tenant.Login} />} />
          <Route path={routes.city.login} element={<PreventedRoute component={CityLogin} meta={meta.city.Login} showOnlyCarpool={showOnlyCarpool} />} />
          <Route path={routes.register.entity} element={<PreventedRoute component={RegisterPagesManager} meta={meta.city.Register} />} />
          <Route path={routes.organisation.forgotPassword} element={<PreventedRoute component={OrganisationForgotPassword} meta={meta.organisation.ForgotPassword} />} />

          <Route path={routes.tenant.profile} element={<ProtectedRoute component={ProfileTenant} meta={meta.tenant.Profile} redirectTo={routes.tenant.login} />} />
          <Route path={routes.tenant.tenantKBS} element={<ProtectedRoute component={TenantKBS} meta={meta.tenant.tenantKBS} redirectTo={routes.tenant.login} />} />
          <Route path={routes.tenant.profileById} element={<ProtectedRoute component={ProfileTenant} meta={meta.tenant.Profile} redirectTo={routes.tenant.login} />} />
          <Route path={routes.tenant.addProfile} element={<ProtectedRoute component={TenantRegisterByAdminScreen} meta={meta.tenant.Dashboard} redirectTo={routes.tenant.login} />} />

          <Route path="/tenant" element={<ProtectedRoute component={TenantRoutes} meta={meta.tenant.Dashboard} redirectTo={routes.tenant.login} />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<TenantDashboard />} />
            <Route path="dashboard/orders" element={<TenantOrders />} />
            <Route path="dashboard/orders/:orderId" element={<TenantOrder />} />
            <Route path="dashboard/products" element={<TenantProducts />} />
            <Route path="dashboard/products/add" element={<TenantAddProduct />} />
            <Route path="dashboard/products/edit/:productID" element={<TenantEditProduct />} />
            <Route path="dashboard/products/edit/:productId/barCodes" element={<TenantProductBarcodes />} />
            <Route path="tenantKBS" element={<TenantKBS />} />
            <Route path="dashboard/stores" element={<TenantStores />} />
          </Route>

          <Route path="/organisation" element={<OrganizationRoutes />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<ProtectedRoute component={OrganisationDashboard} meta={meta.organisation.Dashboard} />} />
            <Route path="users/:userID" element={<ProtectedRoute component={UserPage} meta={meta.organisation.Dashboard} />} />
            <Route path="profile" element={<ProtectedRoute component={ProfileOrganisation} meta={meta.organisation.Profile} />} />
            <Route path="challenges/update/:id" element={<ProtectedRoute component={ChallengeOrganisationEdit} meta={meta.organisation.ChallengeEdit} />} />
            <Route path="challenges/info/:id" element={<ProtectedRoute component={ChallengeOrganisationInfo} meta={meta.organisation.ChallengeInfo} />} />
            <Route path="challenges/create" element={<ProtectedRoute component={ChallengeOrganisationCreate} meta={meta.organisation.ChallengeCreate} />} />
            <Route path="challenges/create-for-another-organisation" element={<ProtectedRoute component={ChallengeOrganisationCreateForAnotherOrg} meta={meta.organisation.ChallengeCreate} />} />
            <Route path="challenges/inter-organisation-guide" element={<ProtectedRoute component={InterOrganisationChallengeGuide} meta={meta.organisation.ChallengeInterOrganisationGuide} />} />
            <Route path="challenges/duel/:id" element={<ProtectedRoute component={ChallengeOrganisationMobilityDuel} meta={meta.organisation.ChallengeMobilityDuel} />} />
            <Route path="challenges/dashboard" element={<ProtectedRoute component={ChallengeOrganisationDashboard} meta={meta.organisation.ChallengeDashboard} />} />
            <Route path="challenges" element={<ProtectedRoute component={ChallengeOrganisationList} meta={meta.organisation.ChallengeList} />} />
            <Route path="FAQ" element={<ProtectedRoute component={OrganisationFAQ} meta={meta.organisation.FAQ} />} />
            <Route path="settings" element={<ProtectedRoute component={OrganizationSettings} meta={meta.organisation.Settings} />} />
            <Route path="branches" element={<ProtectedRoute component={Branch} meta={meta.organisation.Branch} />} />
            <Route path="notifications" element={<ProtectedRoute component={Notification} meta={meta.organisation.Notification} />} />
            <Route path="emails" element={<ProtectedRoute component={Email} meta={meta.organisation.Email} />} />
            <Route path="notifications/:id" element={<ProtectedRoute component={NotificationDetails} meta={meta.organisation.NotificationDetails} />} />
            <Route path="emails/:id" element={<ProtectedRoute component={Email} meta={meta.organisation.EmailDetails} />} />
            <Route path="branches/:id" element={<ProtectedRoute component={BranchDetails} meta={meta.organisation.BranchDetails} />} />
            <Route path="carpool-events/:id" element={<ProtectedRoute component={SingleCarpoolEvent} meta={meta.organisation.Dashboard} />} />
            <Route path="carpool-events" element={<ProtectedRoute component={CarpoolEvents} meta={meta.organisation.Dashboard} />} />
            <Route path="zones" element={<ProtectedRoute component={OrganisationZones} meta={meta.organisation.Dashboard} />} />
            <Route path="heat-map" element={<ProtectedRoute component={CitySessionsHeatMap} meta={meta.organisation.Dashboard} />} />
          </Route>

          <Route path="/city" element={<CityRoutes />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<ProtectedRoute component={CityDashboard} meta={meta.city.Dashboard} />} />
            <Route path="challenges/update/:id" element={<ProtectedRoute component={ChallengeCityEdit} meta={meta.city.ChallengeEdit} />} />
            <Route path="challenges/info/:id" element={<ProtectedRoute component={ChallengeCityInfo} meta={meta.city.ChallengeInfo} />} />
            <Route path="challenges/create" element={<ProtectedRoute component={ChallengeCityCreate} meta={meta.city.ChallengeCreate} />} />
            <Route path="challenges/dashboard" element={<ProtectedRoute component={ChallengeCityDashboard} meta={meta.city.ChallengeDashboard} />} />
            <Route path="challenges" element={<ProtectedRoute component={ChallengeCityList} meta={meta.city.ChallengeList} />} />
            <Route path="profile" element={<ProtectedRoute component={ProfileCity} meta={meta.city.Profile} />} />
            <Route path="postalCodeMap" element={<ProtectedRoute component={PostalCodeMap} meta={meta.city.postalCodeMap} />} />
            <Route path="FAQ" element={<ProtectedRoute component={OrganisationFAQ} meta={meta.city.FAQ} />} />
            <Route path="KBS" element={<ProtectedRoute component={CityKBS} meta={meta.city.KBS} />} />
            <Route path="manage" element={<ProtectedRoute component={CityManage} meta={meta.city.manage} />} />
            <Route path="branches" element={<ProtectedRoute component={Branch} meta={meta.city.Branch} />} />
            <Route path="notifications" element={<ProtectedRoute component={Notification} meta={meta.city.Notification} />} />
            <Route path="categories" element={<ProtectedRoute component={Categories} meta={meta.city.category} />} />
            <Route path="emails" element={<ProtectedRoute component={Email} meta={meta.city.Email} />} />
            <Route path="mailerlite" element={<ProtectedRoute component={CityMailerLite} meta={meta.city.Email} />} />
            <Route path="badges" element={<ProtectedRoute component={Badge} meta={meta.city.Badge} />} />
            <Route path="challenge-recommendation-rules" element={<ProtectedRoute component={ChallengeRecommendationRules} meta={meta.city.ChallengeRecommendationRules} />} />
            <Route path="news" element={<ProtectedRoute component={News} meta={meta.city.News} />} />
            <Route path="happy_hours" element={<ProtectedRoute component={HappyHours} meta={meta.city.HappyHours} />} />
            <Route path="news/:id" element={<ProtectedRoute component={NewsDetails} meta={meta.city.NewsDetails} />} />
            <Route path="notifications/:id" element={<ProtectedRoute component={NotificationDetails} meta={meta.city.NotificationDetails} />} />
            <Route path="emails/:id" element={<ProtectedRoute component={Email} meta={meta.city.Email} />} />
            <Route path="branches/:id" element={<ProtectedRoute component={BranchDetails} meta={meta.city.BranchDetails} />} />
            <Route path="settings" element={<ProtectedRoute component={CitySettings} meta={meta.city.settings} />} />
            <Route path="integration-checklist" element={<ProtectedRoute component={ClientIntegrationChecklist} meta={meta.city.settings} />} />
            <Route path="maps" element={<ProtectedRoute component={CityMaps} meta={meta.city.Dashboard} />} />
            <Route path="heat-map" element={<ProtectedRoute component={CitySessionsHeatMap} meta={meta.city.Dashboard} />} />
            <Route path="heat-map-legacy" element={<ProtectedRoute component={CitySessionsHeatMapLegacy} meta={meta.city.Dashboard} />} />
            <Route path="reports" element={<ProtectedRoute component={ReportsDashboard} meta={meta.city.Reports} />} />
            <Route path="mobile-reports" element={<ProtectedRoute component={MobileReportsDashboard} meta={meta.city.MobileReports} />} />
            <Route path="social-dashboard" element={<ProtectedRoute component={SocialDashboard} meta={meta.city.SocialDashboard} />} />
            <Route path="users/:userID" element={<ProtectedRoute component={UserPage} meta={meta.city.Dashboard} />} />
            <Route path="organisations/:organisationId" element={<ProtectedRoute component={OrganisationPage} meta={meta.city.Dashboard} />} />
            <Route path="users" element={<ProtectedRoute component={CityUsers} meta={meta.city.Dashboard} />} />
            <Route path="users-sessions" element={<ProtectedRoute component={CityUsersSessions} meta={meta.city.Dashboard} />} />
            <Route path="organisations" element={<ProtectedRoute component={CityOrganisations} meta={meta.city.Dashboard} />} />
            <Route path="carpool-events/:id" element={<ProtectedRoute component={SingleCarpoolEvent} meta={meta.city.Dashboard} />} />
            <Route path="carpool-events" element={<ProtectedRoute component={CarpoolEvents} meta={meta.city.Dashboard} />} />
          </Route>
          {/* TODO: Need to check all Routes */}

          {/* <Route exact path="/user-login" component={UserLogin} /> */}
          {/* <Route exact path="/forgot_password" component={ForgotPassword} /> */}
          {/* <Route exact path="/reset_password/:token" component={ResetPassword} /> */}
          {/* <Route exact path="/registration" component={Register} /> */}
          {/* <Route exact path="/register/user" component={RegisterUser} /> */}
          {/* <Route exact path="/register/cgd" component={RegisterCGD} /> */}
          {/* <Route exact component={wrappedRoutes} /> */}
          {/* <Route exact path="/user/dashboard" component={UserDashboard} /> */}
          {/* <Route exact path="/CGD/dashboard" component={CGDDashboard} />
				<Route exact path="/user/sessions" component={Session} />
				<Route exact path="/user/FAQ" component={UserFAQ} />
				<Route
					exact
					path="/user/session/update/:id"
					component={SessionUpdate}
				/>
				
				<Route exact path="/data-tranfert" component={TransferData} /> */}

          {/* TODO: Create a 404 Page */}
          <Route path="*" element={<RegularRoute component={() => <p>Page not found</p>} meta={meta[404]} />} />
        </Routes>
      </Suspense>
    </MainWrapper>
  );
};

const LogoutRoute = () => {
  React.useEffect(() => {
    const doLogout = async () => {
      try {
        await logoutCurrentSession();
      } catch (_) { /* ignore */ }
      try {
        await persistor.purge();
      } catch (_) { /* ignore */ }
      window.location.replace(routes.organisation.login);
    };
    doLogout();
  }, []);
  return <div style={{ padding: 16 }}>Logging out…</div>;
};

// defaultProps removed for function components; using default parameters instead

RegularRoute.propTypes = {
  component: PropType.oneOfType([PropType.func, PropType.shape({})]),
  meta: PropType.shape({
    title: PropType.string,
    description: PropType.string,
  }),
};

// defaultProps removed; using default parameters instead

ProtectedRoute.propTypes = {
  component: PropType.oneOfType([PropType.func, PropType.shape({})]),
  meta: PropType.shape({
    title: PropType.string,
    description: PropType.string,
  }),
};

// defaultProps removed; using default parameters instead

PreventedRoute.propTypes = {
  component: PropType.oneOfType([PropType.func, PropType.shape({})]),
  meta: PropType.shape({
    title: PropType.string,
    description: PropType.string,
  }),
};

TenantPublicRoute.propTypes = {
  component: PropType.oneOfType([PropType.func, PropType.shape({})]),
  meta: PropType.shape({
    title: PropType.string,
    description: PropType.string,
  }),
};

export default Router;
